import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto bg-white p-6 rounded-lg shadow-md">
        

        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">EventIQ - Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8 text-center">Last updated: 12/11/2025</p>
            {/* Developer Information */}
        <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h2 className="text-lg font-semibold text-blue-800 mb-3">Developer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
            <div>
              <span className="font-medium">Developer:</span> Divya HM
            </div>
            <div>
              <span className="font-medium">Company:</span> Binalogy
            </div>
            <div>
              <span className="font-medium">Email:</span> divya@binalogy.in
            </div>
            <div>
              <span className="font-medium">Location:</span> Bangalore, India
            </div>
          </div>
        </div>
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">1. Introduction</h2>
            <p className="text-gray-600 mb-4">
              EventIQ ("we", "our", or "us") is a sustainable digital event management
              platform that enables users to plan, manage, and engage in events
              efficiently through QR-based invitations, RSVP tracking, media sharing,
              and sustainability analytics.
            </p>
            <p className="text-gray-600">
              This Privacy Policy explains how EventIQ collects, uses, discloses, and
              protects your personal information when you use our mobile application
              (the "App"). By downloading, creating an account, or using EventIQ, you
              consent to the collection and use of your data as described in this Policy.
              If you do not agree, please uninstall and stop using the App.
            </p>
          </section>

          {/* Controller / Operator */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">2. Controller / Operator</h2>
            <p className="text-gray-600 mb-4">
              EventIQ is operated and managed by <strong>Binalogy</strong>, based in <strong>Bangalore, India</strong>.
            </p>
            <p className="text-gray-600">
              If you have questions or wish to exercise your privacy rights, contact us at:
              <br />
              📧 <strong>divya@binalogy.in</strong>
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">3. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-700 mb-3">3.1 Information You Provide Directly</h3>
            <ul className="list-disc list-inside text-gray-600 ml-4 mb-6">
              <li><strong>Account Information:</strong> Name, email, phone number (used for account creation, verification, and support).</li>
              <li><strong>Event Information:</strong> Event names, dates, venues, and guest lists created by you.</li>
              <li><strong>Guest Data:</strong> Names and contact details of guests you invite (used solely to send invitations and track RSVPs).</li>
              <li><strong>Media Uploads:</strong> Photos and videos uploaded during events.</li>
              <li><strong>Support Communications:</strong> Any messages or attachments you send to our support team.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mb-3">3.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside text-gray-600 ml-4 mb-6">
              <li><strong>Device and Technical Data:</strong> Device type, operating system and version, app version, device identifiers (e.g., Android ID), IP address, and time zone.</li>
              <li><strong>Usage Analytics:</strong> App activity, feature usage, session duration, crash logs, and performance metrics (to improve reliability and user experience).</li>
              <li><strong>Push Notification Token:</strong> To deliver event updates, RSVP reminders, and operational alerts.</li>
              <li><strong>Approximate Location (Optional):</strong> Used for venue mapping if enabled; precise GPS data is not collected.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mb-3">3.3 Sensitive or Special Category Data</h3>
            <p className="text-gray-600">
              EventIQ does <strong>not intentionally collect sensitive personal data</strong> (such as financial, health, religious, or biometric data). If you include such information in messages, invitations, or uploaded media, you consent to its processing for the purpose submitted.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">4. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">
              We use personal information to:
            </p>
            <ul className="list-disc list-inside text-gray-600 ml-4 mb-4">
              <li>Create and manage your EventIQ account;</li>
              <li>Generate and send QR-based invitations;</li>
              <li>Track RSVPs and event attendance;</li>
              <li>Enable photo and video uploads for shared event memories;</li>
              <li>Provide sustainability analytics (e.g., food waste reduction insights);</li>
              <li>Communicate important service updates or event notifications;</li>
              <li>Improve app performance, security, and user experience;</li>
              <li>Prevent fraud, misuse, or unauthorized access; and</li>
              <li>Comply with applicable legal obligations.</li>
            </ul>
            <p className="text-gray-600">
              We <strong>do not</strong> use your data for targeted advertising or unrelated marketing communications.
            </p>
          </section>

          {/* Legal Basis for Processing */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">5. Legal Basis for Processing</h2>
            <p className="text-gray-600 mb-4">
              Where required by applicable law (e.g., in the EEA or under India's DPDP Act, 2023), we process your data based on:
            </p>
            <ul className="list-disc list-inside text-gray-600 ml-4">
              <li><strong>Performance of a Contract:</strong> To provide the app's core functionality and services;</li>
              <li><strong>Legitimate Interests:</strong> To improve app performance, ensure security, and enhance features;</li>
              <li><strong>Consent:</strong> For specific features like contact access, photo uploads, or push notifications;</li>
              <li><strong>Legal Obligations:</strong> To comply with applicable laws or respond to lawful requests.</li>
            </ul>
          </section>

          {/* Sharing and Disclosure */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">6. Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">
              EventIQ does <strong>not sell or trade your personal information.</strong>
              We share information only as necessary to operate the app or as required by law:
            </p>
            <ul className="list-disc list-inside text-gray-600 ml-4">
              <li><strong>Service Providers:</strong> With trusted vendors who help deliver app functionality (e.g., Firebase for cloud storage, push notifications, and analytics). These parties are bound by confidentiality and data protection agreements.</li>
              <li><strong>Legal Requirements:</strong> To comply with lawful requests, prevent fraud, or protect rights, property, or safety.</li>
              <li><strong>Business Transfers:</strong> If EventIQ is merged, acquired, or reorganized, your information may be transferred as part of that transaction, subject to notice and safeguards.</li>
            </ul>
          </section>

          {/* Third-Party Links and SDKs */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">7. Third-Party Links and SDKs</h2>
            <p className="text-gray-600">
              EventIQ may contain links to third-party websites or use SDKs (e.g., Firebase Analytics, Google Maps).
              These third parties operate under their own privacy policies and terms.
              We are not responsible for their practices. Please review their privacy policies before using linked services.
            </p>
          </section>

          {/* International Transfers */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">8. International Transfers</h2>
            <p className="text-gray-600">
              Your information may be stored and processed in <strong>India</strong> or other countries where our service providers operate.
              We ensure appropriate safeguards (such as contractual protections) for any cross-border data transfers as required by applicable law.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">9. Data Retention</h2>
            <p className="text-gray-600 mb-4">
              We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Policy or as required by law.
            </p>
            
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">Category</th>
                    <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50">
                    <td className="py-3 px-4 border-b text-gray-600">Account data (name, contact, login info)</td>
                    <td className="py-3 px-4 border-b text-gray-600">While account is active + up to 12 months post-deletion</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="py-3 px-4 border-b text-gray-600">Guest and event data</td>
                    <td className="py-3 px-4 border-b text-gray-600">Until deleted by the event organizer or after account deletion</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="py-3 px-4 border-b text-gray-600">Analytics and technical logs</td>
                    <td className="py-3 px-4 border-b text-gray-600">Up to 24 months</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="py-3 px-4 border-b text-gray-600">Support inquiries</td>
                    <td className="py-3 px-4 border-b text-gray-600">Up to 3 years</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="py-3 px-4 border-b text-gray-600">Media (photos, videos)</td>
                    <td className="py-3 px-4 border-b text-gray-600">Until user deletes them or account is deleted</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <p className="text-gray-600">
              You can request deletion of your account or associated data at any time by contacting <strong>divya@binalogy.in</strong>.
            </p>
          </section>

          {/* Security Measures */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">10. Security Measures</h2>
            <p className="text-gray-600 mb-4">
              We implement appropriate <strong>technical and organizational measures</strong> to safeguard personal data, including:
            </p>
            <ul className="list-disc list-inside text-gray-600 ml-4 mb-4">
              <li>Encryption (HTTPS/TLS) for data transmission</li>
              <li>Authentication and access controls</li>
              <li>Secure storage on Google Cloud / Firebase</li>
              <li>Regular security audits</li>
            </ul>
            <p className="text-gray-600">
              Despite our efforts, no system is fully secure. In case of a data breach, we will notify affected users and authorities as required by law.
            </p>
          </section>

          {/* Your Rights and Choices */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">11. Your Rights and Choices</h2>
            <p className="text-gray-600 mb-4">
              Under applicable law, including India's DPDP Act, 2023, you have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-600 ml-4 mb-4">
              <li>Access and obtain a copy of your personal data;</li>
              <li>Correct inaccurate information;</li>
              <li>Delete or anonymize your data;</li>
              <li>Withdraw consent at any time (affecting only future processing);</li>
              <li>Request information about data processing and third-party sharing.</li>
            </ul>
            <p className="text-gray-600">
              You may exercise these rights by contacting us at <strong>divya@binalogy.in</strong>. We may verify your identity before processing your request.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">12. Children's Privacy</h2>
            <p className="text-gray-600">
              EventIQ is <strong>not intended for children under 13 years of age</strong> (or under 16 in jurisdictions where consent is required).
              We do not knowingly collect data from minors. If you believe we have inadvertently collected such information, contact us to delete it promptly.
            </p>
          </section>

          {/* Opt-Outs; Push Notifications */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">13. Opt-Outs; Push Notifications</h2>
            <p className="text-gray-600">
              You can disable push notifications at any time via your device settings.
              Note: Disabling notifications may prevent delivery of RSVP reminders or event updates.
              EventIQ does not send marketing notifications or promotional messages.
            </p>
          </section>

          {/* Cookies and Similar Technologies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">14. Cookies and Similar Technologies</h2>
            <p className="text-gray-600">
              EventIQ does not use traditional cookies.
              However, analytics SDKs (such as Firebase) may collect anonymized usage metrics and device identifiers for crash reporting and performance analysis.
            </p>
          </section>

          {/* Data Controllers / Processors and Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">15. Data Controllers / Processors and Contact Information</h2>
            <p className="text-gray-600 mb-2">
              <strong>Controller:</strong> Binalogy (Developer of EventIQ)
            </p>
            <p className="text-gray-600 mb-2">
              <strong>Contact Email:</strong> divya@binalogy.in
            </p>
            <p className="text-gray-600 mb-2">
              <strong>Address:</strong> Bangalore, India
            </p>
            <p className="text-gray-600">
              Please include "Privacy Request -- EventIQ" in your subject line for faster processing.
            </p>
          </section>

          {/* Changes to this Privacy Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">16. Changes to this Privacy Policy</h2>
            <p className="text-gray-600">
              We may update this Policy periodically to reflect legal or operational changes.
              Material updates will be communicated through in-app notices or email, and the "Last Updated" date will be revised.
              Continued use of the app constitutes acceptance of the updated Policy.
            </p>
          </section>

          {/* Complaints and Supervisory Authorities */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">17. Complaints and Supervisory Authorities</h2>
            <p className="text-gray-600">
              If you believe we have not properly addressed your privacy concern, you may file a complaint under the <strong>Digital Personal Data Protection Act, 2023</strong> with the <strong>Data Protection Board of India</strong> or other relevant authority in your jurisdiction.
            </p>
          </section>

          {/* Miscellaneous */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">18. Miscellaneous</h2>
            <p className="text-gray-600">
              This Policy applies only to the EventIQ app and not to any offline or unrelated data collection.
              Your entries in the Play Store "Data Safety" section should align with this Policy.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">19. Contact</h2>
            <p className="text-gray-600 mb-2">
              For questions, requests, or complaints about this Privacy Policy or your personal data, contact:
            </p>
            <p className="text-gray-600 mb-2">
              📧 <strong>Email:</strong> divya@binalogy.in
            </p>
            <p className="text-gray-600">
              📍 <strong>Location:</strong> Bangalore, India
            </p>
          </section>

          
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;